import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/district-templates/{id}';
type THIS_API = ApiDefinition[typeof PATH_TEMPLATE]['GET'];
type Input = THIS_API['params'] & Partial<THIS_API['query']>;
type Output = THIS_API['response'];

/** Filters that can be applied when fetching a district template by id. */
export type DistrictTemplateFilters = {
  chapter?: string;
  joint?: boolean;
};

/**
 * SWR-based hook for fetching a single district template by id, with optional chapter and joint filters.
 * Returns cached data across remounts to prevent unnecessary reloads.
 */
export const useDistrictTemplate = (
  params: (THIS_API['params'] & DistrictTemplateFilters) | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output> => {
  const key = useMemo((): SwrKey => {
    if (!params?.id) return null;
    const { id, chapter, joint } = params;
    const path = buildPath<THIS_API>(PATH_TEMPLATE, { id });
    return [path, { params: { chapter, joint } }];
  }, [params]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * Fetches a single district template by its id.
 * @deprecated Please use `useDistrictTemplate` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getDistrictTemplateById = async ({ id, chapter, joint }: Input): Promise<Output> => {
  const path = buildPath<THIS_API>(PATH_TEMPLATE, { id });
  const response = await noodleApiClient.get(path, { params: { chapter, joint } });
  return response.data;
};

export default getDistrictTemplateById;
